import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { articleApi } from '../services/api';
import { Article } from '../types';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ArticleCard } from '../components/ArticleCard';
import { QuickSummary } from '../components/editorial/QuickSummary';
import { MarketImpact } from '../components/editorial/MarketImpact';
import { ArticleTimeline } from '../components/editorial/ArticleTimeline';
import { SourcesList } from '../components/editorial/SourcesList';
import { ContextualGlossary } from '../components/editorial/ContextualGlossary';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';
const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Crypto News Hub';

export const ArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      loadArticle(slug);
    }
  }, [slug]);

  const loadArticle = async (slug: string) => {
    setLoading(true);
    try {
      const articleData = await articleApi.getArticleBySlug(slug);
      setArticle(articleData);

      // Load related articles
      const related = await articleApi.getRelatedArticles(articleData.id, 4);
      setRelatedArticles(related);
    } catch (error) {
      console.error('Failed to load article:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Carregando artigo...</div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Artigo não encontrado</h1>
          <Link to="/" className="text-blue-600 hover:underline">
            Voltar para o início
          </Link>
        </div>
      </div>
    );
  }

  const displayTitle = article.titlePt || article.title;
  const timeAgo = formatDistanceToNow(new Date(article.publishedAt), { addSuffix: true, locale: ptBR });
  const formattedDate = format(new Date(article.publishedAt), "d 'de' MMMM 'de' yyyy", { locale: ptBR });
  const canonicalUrl = `${SITE_URL}/article/${article.slug}`;
  const wordCount = article.content.replace(/<[^>]+>/g, '').split(/\s+/).length;
  const readingTimeMin = Math.max(1, Math.round(wordCount / 200));
  const metaDescription = article.summaryPt || article.excerpt || displayTitle;

  // JSON-LD structured data (NewsArticle)
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: displayTitle,
    ...(article.titlePt ? { alternativeHeadline: article.title } : {}),
    description: metaDescription,
    url: canonicalUrl,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    image: article.imageUrl || undefined,
    author: article.editorialAuthor
      ? {
          '@type': article.editorialAuthor.isTeam ? 'Organization' : 'Person',
          name: article.editorialAuthor.name,
          url: `${SITE_URL}/autores/${article.editorialAuthor.slug}`,
        }
      : article.author
      ? { '@type': 'Person', name: article.author }
      : { '@type': 'Organization', name: article.sourceName },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    inLanguage: 'pt-BR',
    isAccessibleForFree: true,
  };

  return (
    <>
      <Helmet>
        <title>{displayTitle} | {SITE_NAME}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={displayTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:site_name" content={SITE_NAME} />
        {article.imageUrl && <meta property="og:image" content={article.imageUrl} />}
        <meta property="article:published_time" content={article.publishedAt} />
        <meta property="article:modified_time" content={article.updatedAt} />
        {article.author && <meta property="article:author" content={article.author} />}
        {article.category && <meta property="article:section" content={article.category.name} />}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={displayTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {article.imageUrl && <meta name="twitter:image" content={article.imageUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <article className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Breadcrumb */}
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            {article.category && (
              <>
                <span>›</span>
                <Link to={`/category/${article.category.slug}`} className="hover:text-blue-600 transition">
                  {article.category.name}
                </Link>
              </>
            )}
            <span>›</span>
            <span className="text-gray-700 truncate max-w-xs">{displayTitle}</span>
          </nav>

          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
            {/* Category */}
            {article.category && (
              <Link
                to={`/category/${article.category.slug}`}
                className="inline-block text-xs font-bold text-blue-600 hover:text-blue-800 uppercase tracking-wider bg-blue-50 px-3 py-1 rounded-full mb-4"
              >
                {article.category.name}
              </Link>
            )}

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 leading-tight">{displayTitle}</h1>
            {article.subtitle && (
              <p className="text-lg text-gray-500 leading-relaxed mb-4">{article.subtitle}</p>
            )}

            {/* Meta / byline */}
            <div className="flex flex-wrap items-center text-sm text-gray-500 gap-x-4 gap-y-1 mb-6">
              {article.editorialAuthor && (
                <Link
                  to={`/autores/${article.editorialAuthor.slug}`}
                  className="font-semibold text-gray-700 hover:text-blue-600 transition"
                >
                  Por {article.editorialAuthor.name}
                </Link>
              )}
              {article.reviewer && <span>revisão: {article.reviewer.name}</span>}
              <span>{formattedDate}</span>
              <span>·</span>
              <span>{timeAgo}</span>
              <span>·</span>
              <span>{readingTimeMin} min de leitura</span>
              {article.updatedAt !== article.createdAt && (
                <span className="text-gray-400">
                  atualizado em {format(new Date(article.updatedAt), 'dd/MM/yyyy', { locale: ptBR })}
                </span>
              )}
            </div>

            {/* Image */}
            {article.imageUrl && (
              <img
                src={article.imageUrl}
                alt={displayTitle}
                className="w-full rounded-lg mb-6"
              />
            )}

            {/* Risk Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-6 text-xs text-amber-800 leading-relaxed">
              <strong>⚠️ Aviso:</strong> Este conteúdo é meramente informativo e não constitui aconselhamento financeiro ou de investimento.
              Investimentos em criptomoedas envolvem alto risco e volatilidade. O valor dos ativos pode cair a zero.
              Nunca invista mais do que pode perder. Consulte um profissional qualificado antes de tomar decisões financeiras.
            </div>

            {/* Resumo em 30 segundos (novo modelo) ou resumo simples (legado) */}
            {article.quickSummary && article.quickSummary.length > 0 ? (
              <QuickSummary points={article.quickSummary} />
            ) : null}
            {(!article.quickSummary || article.quickSummary.length === 0) && article.summaryPt && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🇧🇷</span>
                  <span className="text-blue-800 font-bold text-sm uppercase tracking-wider">
                    O que você precisa saber
                  </span>
                </div>
                <p className="text-gray-800 leading-relaxed text-base">{article.summaryPt}</p>
              </div>
            )}

            {/* Blocos editoriais (novo modelo) */}
            {article.whatHappened && (
              <div className="space-y-6 mb-8">
                {[
                  { title: 'O que aconteceu', text: article.whatHappened },
                  { title: 'Por que isso importa', text: article.whyItMatters },
                  { title: 'Contexto', text: article.context },
                  { title: 'Quem pode ser afetado', text: article.whoIsAffected },
                  { title: 'O que ainda não sabemos', text: article.whatWeDontKnow },
                  { title: 'Principais riscos', text: article.keyRisks },
                ]
                  .filter((block) => block.text)
                  .map((block) => (
                    <section key={block.title}>
                      <h2 className="text-xl font-bold text-gray-900 mb-2">{block.title}</h2>
                      <p className="text-gray-700 leading-relaxed">{block.text}</p>
                    </section>
                  ))}
              </div>
            )}

            {/* Cobertura completa (tradução) */}
            {article.whatHappened && (article.contentPt || article.content) && (
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-t pt-6">Cobertura completa</h2>
            )}
            <div
              className="prose max-w-none mb-6 text-lg text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.contentPt || article.content }}
            />
            {!article.contentPt && (
              <p className="text-xs text-gray-400 mb-6 italic">
                Conteúdo original em inglês. A tradução automática será disponibilizada em breve.
              </p>
            )}

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {article.tags.map((articleTag) => (
                  <Link
                    key={articleTag.tag.id}
                    to={`/tag/${articleTag.tag.slug}`}
                    className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-gray-200 transition"
                  >
                    #{articleTag.tag.name}
                  </Link>
                ))}
              </div>
            )}

            {/* Share */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm text-gray-500 font-medium">Compartilhar:</span>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(displayTitle + ' ' + canonicalUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold px-4 py-2 rounded-full transition"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.554 4.118 1.527 5.845L0 24l6.345-1.504A11.955 11.955 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.891 0-3.66-.498-5.193-1.372l-.372-.215-3.866.916.95-3.768-.235-.387A9.953 9.953 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
                </svg>
                WhatsApp
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(displayTitle)}&url=${encodeURIComponent(canonicalUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-black hover:bg-gray-800 text-white text-sm font-semibold px-4 py-2 rounded-full transition"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                X
              </a>
            </div>

            {/* AI transparency */}
            {(article.contentPt || article.summaryPt || article.titlePt) && (
              <p className="text-xs text-gray-400 mb-4 leading-relaxed">
                🤖 Tradução e resumo produzidos com auxílio de inteligência artificial e sujeitos a revisão
                editorial, conforme nossa{' '}
                <Link to="/politica-editorial" className="text-gray-500 underline hover:text-blue-600">
                  Política Editorial
                </Link>
                . Encontrou um erro?{' '}
                <Link to="/politica-de-correcoes" className="text-gray-500 underline hover:text-blue-600">
                  Saiba como reportar
                </Link>
                .
              </p>
            )}

            {/* Source attribution */}
            <div className="border-t pt-5 mt-4">
              <p className="text-xs text-gray-400 mb-2">
                Fonte original{article.author ? ` — por ${article.author}` : ''}
              </p>
              <a
                href={article.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-blue-600 text-sm inline-flex items-center gap-1 transition"
              >
                {article.sourceName}
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>

          {/* Impacto no mercado */}
          {article.impacts && article.impacts.length > 0 && <MarketImpact impacts={article.impacts} />}

          {/* Linha do tempo */}
          {article.timelineEvents && article.timelineEvents.length > 0 && (
            <ArticleTimeline events={article.timelineEvents} />
          )}

          {/* Fontes utilizadas */}
          {article.sources && article.sources.length > 0 && <SourcesList sources={article.sources} />}

          {/* Glossário contextual */}
          <ContextualGlossary
            texts={[
              displayTitle,
              article.subtitle,
              article.summaryPt,
              article.whatHappened,
              article.whyItMatters,
              article.context,
              article.keyRisks,
            ]}
          />

          {/* Glossário CTA */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 mb-6 flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-blue-900 text-sm mb-0.5">Novo no mundo cripto?</p>
              <p className="text-blue-700 text-xs">Consulte nosso glossário com 25+ termos explicados em português.</p>
            </div>
            <Link
              to="/glossario"
              className="flex-shrink-0 bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Ver Glossário
            </Link>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Artigos Relacionados</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedArticles.map((relatedArticle) => (
                  <ArticleCard key={relatedArticle.id} article={relatedArticle} />
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    </>
  );
};
